import React from 'react';

import {
  Container,
  BarContainer,
  Title,
  ProgressBar,
  Percentage,
  LoaderWrapper,
} from './style';
import Loader from '../../../components/Loader/Loader';
import NoDataFound from '../../../components/redesign/NoDataFound';
import useCustomMediaQuery from '@utils/useCustomMediaQuery';
import { screenSizes } from '@constants/Constants';

interface ProgressBarItem {
  title: string;
  color: string;
  percentage?: number; // Optional for backward compatibility
  value?: number; // New field for raw values
}

interface HorizontalProgressBarListProps {
  items: ProgressBarItem[];
  isLoading?: boolean;
  hasNoData?: boolean;
  equalVerticalSpacing?: boolean;
  wrapText?: boolean; // Optional prop to control text wrapping
  fixedHeight?: number; // Optional prop to set fixed height in pixels and enable vertical scrolling
  displayMode?: 'percentage' | 'value'; // Default: 'percentage'
  valueFormatter?: (value: number) => string; // Custom formatting function
}

export const HorizontalProgressBarList: React.FC<
  HorizontalProgressBarListProps
> = ({
  items,
  isLoading = false,
  hasNoData = false,
  equalVerticalSpacing = false,
  wrapText = false,
  fixedHeight,
  displayMode = 'percentage',
  valueFormatter,
}) => {
  const isMobile = useCustomMediaQuery(screenSizes.mobile);
  // Calculate max value for relative width computation (before formatting)
  const maxValue =
    displayMode === 'value'
      ? Math.max(...items.map((item) => item.value || 0))
      : 100;

  // Helper function to get bar width percentage
  const getBarWidth = (item: ProgressBarItem): number => {
    if (displayMode === 'percentage') {
      return item.percentage || 0;
    }
    return maxValue > 0 ? ((item.value || 0) / maxValue) * 100 : 0;
  };

  // Helper function to get display value
  const getDisplayValue = (item: ProgressBarItem): string => {
    if (displayMode === 'percentage') {
      return `${item.percentage || 0}%`;
    }
    const rawValue = item.value || 0;
    return valueFormatter ? valueFormatter(rawValue) : String(rawValue);
  };

  // Helper function to format title with character limit
  const formatTitle = (title: string, maxCharsPerLine: number = 32): string => {
    if (title.length <= maxCharsPerLine) {
      return title;
    }

    // Find the best break point (space) near the character limit
    let breakPoint = maxCharsPerLine;
    while (breakPoint > 0 && title[breakPoint] !== ' ') {
      breakPoint--;
    }

    // If no space found, break at character limit
    if (breakPoint === 0) {
      breakPoint = maxCharsPerLine;
    }

    const firstLine = title.substring(0, breakPoint);
    const remainingText = title.substring(breakPoint).trim();

    if (remainingText.length === 0) {
      return firstLine;
    }

    return firstLine + '\n' + formatTitle(remainingText, maxCharsPerLine);
  };

  return (
    <Container
      equalVerticalSpacing={equalVerticalSpacing}
      fixedHeight={fixedHeight}
    >
      {isLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : hasNoData ? (
        <NoDataFound />
      ) : (
        items?.map((item, index) => {
          return (
            <BarContainer key={`progress-bar-${index}`}>
              <Title wrapText={wrapText} title={item.title}>
                {formatTitle(item.title, isMobile ? 15 : 32)}
              </Title>
              <ProgressBar color={item.color} percentage={getBarWidth(item)} />
              <Percentage>{getDisplayValue(item)}</Percentage>
            </BarContainer>
          );
        })
      )}
    </Container>
  );
};

export default HorizontalProgressBarList;
